import { AgsBetBarComponent } from "ags/components/ags-bet-bar-component";
import { AgsHeaderComponent } from "ags/components/ags-header-component";
import { AgsPreloaderComponent } from "ags/components/ags-preloader-component";
import { AgsPromptComponent } from "ags/components/ags-prompt-component";
import { AgsSymbolFooterComponent } from "ags/components/ags-symbol-footer-component";
import { AgsPaytableFactory } from "ags/components/factory/ags-paytable-component-factory";
import { LoopWinsComponent } from "ags/components/loop-wins-component";
import { AgsForceTab } from "ags/debug/menu/tabs/ags-force-tab";
import { AuthRecordBuilder } from "ags/integration/builders/records/auth-record-builder";
import { SpinRecordBuilder } from "ags/integration/builders/records/spin-record-builder";
import { AuthRequestBuilder } from "ags/integration/builders/requests/auth-request-builder";
import { PickRequestBuilder } from "ags/integration/builders/requests/pick-request-builder";
import { SpinRequestBuilder } from "ags/integration/builders/requests/spin-request-builder";
import { AgsTrailStateResultBuilder } from "ags/integration/builders/results/firework-state-result-builder";
import { PickTriggerResultBuilder } from "ags/integration/builders/results/pick-trigger-result-builder";
import { PretellResultBuilder } from "ags/integration/builders/results/pretell-result-builder";
import { ScatterResultBuilder } from "ags/integration/builders/results/scatter-result-builder";
import { SparkResultBuilder } from "ags/integration/builders/results/spark-result-builder";
import { agsSpinForcer } from "ags/integration/precan/ags-spin-forcer";
import { AgsSetup } from "ags/setup/ags-setup";
import { Components } from "appworks/components/components";
import { HTMLText } from "appworks/graphics/pixi/html-text";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { builderManager } from "appworks/server/builders/builder-manager";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { UIFlagState } from "appworks/ui/flags/ui-flag-state";
import { UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { UI } from "appworks/ui/ui";
import { deviceInfo } from "appworks/utils/device-info";
import { ThemeMatrixFactory } from "components/factories/theme-matrix-factory";
import { ThemeJackpotDisplayComponent } from "components/theme-jackpot-display-component";
import { gameLayers } from "game-layers";
import { ThemePickRecordBuilder } from "integration/records/theme-pick-record-builder";
import { ThemeRecoveryRecordBuilder } from "integration/records/theme-recovery-record-builder";
import { ThemeGridSizeResultBuilder } from "integration/results/theme-grid-size-result";
import { ThemeHarpoonResultBuilder } from "integration/results/theme-harpoon-result";
import { ThemeSoundSetup } from "setup/theme-sound-setup";
import { ThemeStateSetup } from "setup/theme-state-setup";
import { AlertComponent } from "slotworks/components/alert/alert-component";
import { GenericComponentFactory } from "slotworks/components/factories/generic-component-factory";
import { AnticipationSkipMode, SpinReelTransition } from "slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition";
import { PayoutMode } from "slotworks/components/paytable/paytable-component";
import { DebugMenu } from "slotworks/debug/menu/debug-menu";
import { AnticipationSupplement } from "slotworks/model/gameplay/supplements/anticipation-supplement";
import { ThemePadGridSupplement } from "supplements/theme-pad-grid-supplement";
import { ThemeGameDefinitionSetup } from "./theme-game-definition-setup";
import { ThemeLayerSetup } from "./theme-layer-setup";
import { ThemeCelebrateWinComponent } from "components/theme-celebrate-win-component";
import { ThemeLineResultBuilder } from "integration/results/theme-line-result-builder";
import { ThemeAnimateWinsComponent } from "components/theme-animate-wins-component";
import { BigWinComponent } from "ags/components/big-win-component";

export class ThemeSetup extends AgsSetup {
    public setupComponents(): void {
        SpinReelTransition.anticipationSkipMode = AnticipationSkipMode.NEVER;

        Components.register(new AgsPaytableFactory({
            payoutMode: PayoutMode.MULTIPLIER_ONLY,
            uiLayerName: "MenusContent",
            contentLayerName: "PaytableContent"
        }));

        Components.register(new GenericComponentFactory(AgsBetBarComponent));
        Components.register(new GenericComponentFactory(ThemeAnimateWinsComponent, false));
        Components.register(new GenericComponentFactory(LoopWinsComponent));
        Components.register(new GenericComponentFactory(AgsHeaderComponent));
        Components.register(new GenericComponentFactory(ThemeCelebrateWinComponent));
        Components.register(new GenericComponentFactory(BigWinComponent));


        Components.register(new ThemeMatrixFactory());
        Components.register(new GenericComponentFactory(ThemeJackpotDisplayComponent));
        Components.register(new GenericComponentFactory(AgsSymbolFooterComponent));

        Components.register(new GenericComponentFactory(AlertComponent), 0);
        Components.register(new GenericComponentFactory(AgsPromptComponent, { usePromptLayerAsClickListener: true }), 0);
        Components.register(new GenericComponentFactory(AgsPreloaderComponent), 0);

        // To stop text wrapping on preloader, don't know where better to put this
        HTMLText.minFontSize = 2;
    }

    public setupComms(): void {
        super.setupComms();

        builderManager.addRequestBuilder(new AuthRequestBuilder());
        builderManager.addRequestBuilder(new SpinRequestBuilder());
        builderManager.addRequestBuilder(new PickRequestBuilder());

        builderManager.addRecordBuilder(new AuthRecordBuilder());
        builderManager.addRecordBuilder(new SpinRecordBuilder());
        builderManager.addRecordBuilder(new ThemeRecoveryRecordBuilder());
        builderManager.addRecordBuilder(new ThemePickRecordBuilder());

        builderManager.addResultBuilder(new ScatterResultBuilder());
        builderManager.addResultBuilder(new ThemeLineResultBuilder());
        builderManager.addResultBuilder(new PickTriggerResultBuilder());
        builderManager.addResultBuilder(new SparkResultBuilder());
        builderManager.addResultBuilder(new PretellResultBuilder());
        builderManager.addResultBuilder(new AgsTrailStateResultBuilder());
        builderManager.addResultBuilder(new ThemeHarpoonResultBuilder());
        builderManager.addResultBuilder(new ThemeGridSizeResultBuilder());

        Gameplay.dataProcessorSupplements.remove(
            Gameplay.dataProcessorSupplements.getSupplement(AnticipationSupplement)
        );
        Gameplay.dataProcessorSupplements.addBeforeStandard(new ThemePadGridSupplement());
    }

    public setupDebug(): void {
        super.setupDebug();
        this.setupSpinForces();
    }

    public setupGameDefinition(): void {
        ThemeGameDefinitionSetup();
    }

    public setupI18N(): void {
        super.setupI18N();

        const translations = Services.get(TranslationsService);
        translations.setAlias("pmt.pts", deviceInfo.isDesktop ? "press_play" : "press_prompt_mobile");

        /*gameLayers.Menus.onSceneEnter.add((sceneName: string) => {
            if (sceneName === "preloader") {
                const waysToWinText = gameLayers.Menus.getText("waystowin");
                const value: string = (16807).toLocaleString(gameiomModel.language);
                waysToWinText.text = translations.get("ways_to_win", { ways: value });
            }
        });*/

        HTMLText.setTagStyling("yellow", { fill: 0xfff409, stroke: 0xa44106, strokeThickness: 2 });
        HTMLText.setTagStyling("big", { fontScaleWidth: 1.2, fontScaleHeight: 1.2 });
        HTMLText.setTagStyling("big_spins", { fontScaleWidth: 1.2, fontScaleHeight: 1.2 });
        HTMLText.setTagStyling("boost", {
            fill: [0xfff97a, 0xfff409],
            fillGradientStops: [0.4, 0.9],
            fillGradientType: PIXI.TEXT_GRADIENT.LINEAR_VERTICAL,
            fontScaleWidth: 1.3,
            fontScaleHeight: 1.3
        });
    }

    public setupLayers(): void {
        super.setupLayers();
        ThemeLayerSetup();
    }

    public setupSound(): void {
        super.setupSound();
        ThemeSoundSetup();
    }

    public setupStates(): void {
        ThemeStateSetup();
    }

    public setupUI(): void {
        UI.layer(gameLayers.PaytableBackground, [
            { scene: "base", show: new UIFlagState(UIMenuFlag.PAYTABLE) }
        ]);
        super.setupUI();
    }

    protected setupSpinForces() {
        const forces = new Map<string, string>([
            ["Top Wild Jackpot", String.raw`{
              "Outcomes": [
                {
                  "PrizeSelection": [
                    10000
                  ],
                  "ReelStopSelection": [
                    44,
                    44,
                    16
                  ]
                }
              ]
            }`],
              ["Middle Wild Jackpot", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      2000
                    ],
                    "ReelStopSelection": [
                      34,
                      34,
                      34
                    ]
                  }
                ]
              }`],
              ["Low Wild Jackpot", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      800
                    ],
                    "ReelStopSelection": [
                      10,
                      24,
                      52
                    ]
                  }
                ]
              }`],
              ["Mixed Wild Jackpot", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      250
                    ],
                    "ReelStopSelection": [
                      52,
                      44,
                      6
                    ]
                  }
                ]
              }`],
              ["3 Red 7s", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      60
                    ],
                    "ReelStopSelection": [
                      6,
                      50,
                      48
                    ]
                  }
                ]
              }`],
              ["3 Black 7s", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      40
                    ],
                    "ReelStopSelection": [
                      30,
                      40,
                      36
                    ]
                  }
                ]
              }`],
              ["3 White 7s", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      25
                    ],
                    "ReelStopSelection": [
                      38,
                      36,
                      8
                    ]
                  }
                ]
              }`],
              ["3 Mixed 7s", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      10
                    ],
                    "ReelStopSelection": [
                      6,
                      40,
                      8
                    ]
                  }
                ]
              }`],
              ["3 Mixed 7s w/ Wild", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      10
                    ],
                    "ReelStopSelection": [
                      52,
                      40,
                      20
                    ]
                  }
                ]
              }`],
              ["3 Double Bars", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      5
                    ],
                    "ReelStopSelection": [
                      24,
                      18,
                      54
                    ]
                  }
                ]
              }`],
              ["3 Bars", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      4
                    ],
                    "ReelStopSelection": [
                      36,
                      22,
                      4
                    ]
                  }
                ]
              }`],
              ["3 Mixed Bars", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      3
                    ],
                    "ReelStopSelection": [
                      16,
                      34,
                      50
                    ]
                  }
                ]
              }`],
              ["3 Mixed Bars w/ Wild", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      3
                    ],
                    "ReelStopSelection": [
                      46,
                      30,
                      24
                    ]
                  }
                ]
              }`],
              ["Huge Win", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      800
                    ],
                    "ReelStopSelection": [
                      10,
                      24,
                      52
                    ]
                  }
                ]
              }`],
              ["Big Win", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      250
                    ],
                    "ReelStopSelection": [
                      34,
                      52,
                      52
                    ]
                  }
                ]
              }`],
              ["2 Lines no anim", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      7
                    ],
                    "ReelStopSelection": [
                      4,
                      17,
                      52
                    ]
                  }
                ]
              }`],
              ["2 Lines w/ anim", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      40
                    ],
                    "ReelStopSelection": [
                      50,
                      53,
                      6
                    ]
                  }
                ]
              }`],
              ["Hard Lock Big Win", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      155
                    ],
                    "ReelStopSelection": [
                      13,
                      24,
                      23
                    ]
                  }
                ]
              }`],
              ["Big Win Error Test", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      100
                    ],
                    "ReelStopSelection": [
                      33,
                      6,
                      13
                    ]
                  }
                ]
              }`],
              ["Symbol Dim Test", String.raw`{
                "Outcomes": [
                  {
                    "PrizeSelection": [
                      50
                    ],
                    "ReelStopSelection": [
                      35,
                      7,
                      13
                    ]
                  }
                ]
              }`],
        ]);

        agsSpinForcer.setResults(forces);
        DebugMenu.add(new AgsForceTab("Spin Forces", agsSpinForcer));
    }
}
